/**
 * Get Accounts Use Case
 * Fetches account summary, subscriptions, and payments
 */

import { DashboardRepository } from '../data/dashboard-repository';
import { AccountSummary, Subscription, PaymentToday } from '../types/dashboard-types';

export class GetAccountsUseCase {
  constructor(private readonly repository: DashboardRepository) {}

  async executeSummary(): Promise<{
    summary: AccountSummary;
    hasError: boolean;
    errorMessage?: string;
  }> {
    try {
      const summary = await this.repository.getAccountSummary();

      return {
        summary,
        hasError: false,
      };
    } catch (error) {
      console.error('GetAccountsUseCase.executeSummary failed:', error);

      return {
        summary: this.getEmptySummary(),
        hasError: true,
        errorMessage: error instanceof Error ? error.message : 'Failed to load account summary',
      };
    }
  }

  async executeSubscriptions(page: number = 0, pageSize: number = 10): Promise<{
    subscriptions: Subscription[];
    totalCount: number;
    expiringCount: number;
    hasError: boolean;
    errorMessage?: string;
  }> {
    try {
      const result = await this.repository.getSubscriptions(page, pageSize);
      const subscriptions = result.subscriptions;
      const expiringCount = subscriptions.filter(s => s.isExpiringSoon).length;

      return {
        subscriptions,
        totalCount: result.totalCount,
        expiringCount,
        hasError: false,
      };
    } catch (error) {
      console.error('GetAccountsUseCase.executeSubscriptions failed:', error);

      return {
        subscriptions: [],
        totalCount: 0,
        expiringCount: 0,
        hasError: true,
        errorMessage: error instanceof Error ? error.message : 'Failed to load subscriptions',
      };
    }
  }

  async executePaymentsToday(): Promise<{
    payments: PaymentToday[];
    totalAmount: number;
    failedCount: number;
    hasError: boolean;
    errorMessage?: string;
  }> {
    try {
      const result = await this.repository.getPaymentsToday();

      return {
        payments: result.payments,
        totalAmount: result.totalAmount,
        failedCount: result.failedCount,
        hasError: false,
      };
    } catch (error) {
      console.error('GetAccountsUseCase.executePaymentsToday failed:', error);

      return {
        payments: [],
        totalAmount: 0,
        failedCount: 0,
        hasError: true,
        errorMessage: error instanceof Error ? error.message : 'Failed to load payments',
      };
    }
  }

  private getEmptySummary(): AccountSummary {
    return {
      totalAccounts: 0,
      activeAccounts: 0,
      blockedAccounts: 0,
      pendingAccounts: 0,
      deactivatedAccounts: 0,
      activeSubscriptions: 0,
      expiringSubscriptions: 0,
      expiringPaymentCards: 0,
      expiredPaymentCards: 0,
      paymentsToday: 0,
      successfulPaymentsCountToday: 0,
      failedPaymentsToday: 0,
    };
  }
}
